import { useEffect, useRef, useState, type ReactNode } from "react"

import { isReverificationCancelledError } from "@clerk/nextjs/errors"
import { Badge } from "@coval/ui/badge"
import { Button } from "@coval/ui/button"
import { Card, CardContent } from "@coval/ui/card"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@coval/ui/dialog"
import { Label } from "@coval/ui/label"
import {
  ListTable,
  ListTableBody,
  ListTableCell,
  ListTableHead,
  ListTableHeader,
  ListTableRow,
} from "@coval/ui/list-table"
import { SearchInput } from "@coval/ui/search-input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@coval/ui/tabs"
import { toast } from "@coval/ui/toast"
import { Tooltip } from "@coval/ui/tooltip"
import { Mail, Shield, UserPlus, Users } from "lucide-react"

import { OrganizationPermissionNotice } from "@/components/auth/OrganizationPermissionNotice"

import {
  ORGANIZATION_ROLE_LABELS,
  type MemberManagementActions,
  type MemberManagementData,
  type MemberSummary,
  type OrganizationRole,
} from "../types"
import {
  getInvitationEmailError,
  parseInvitationEmails,
} from "../utils/invitationEmails"
import { InvitationEmailInput } from "./InvitationEmailInput"
import { MemberTable } from "./MemberTable"
import { OrganizationRoleCard } from "./OrganizationRoleCard"

interface UserManagementPanelProps {
  data: MemberManagementData
  actions: MemberManagementActions
  currentUserId: string
  canManage: boolean
  memberSearch: string
  onMemberSearchChange: (value: string) => void
  adminCount: number
  memberCount?: number
  invitationCount?: number
  refreshing: boolean
  membersState: ReactNode
  invitationsState: ReactNode
  membersPagination: ReactNode
  invitationsPagination: ReactNode
}

export function UserManagementPanel({
  data,
  actions,
  currentUserId,
  canManage,
  memberSearch,
  onMemberSearchChange,
  adminCount,
  memberCount,
  invitationCount,
  refreshing,
  membersState,
  invitationsState,
  membersPagination,
  invitationsPagination,
}: UserManagementPanelProps) {
  const [tab, setTab] = useState("members")
  const [inviteOpen, setInviteOpen] = useState(false)
  const [emails, setEmails] = useState<string[]>([])
  const [emailDraft, setEmailDraft] = useState("")
  const [role, setRole] = useState<OrganizationRole>("org:member")
  const [removeTarget, setRemoveTarget] = useState<MemberSummary | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const invitationEmails = parseInvitationEmails([...emails, emailDraft])
  const invitationEmailError = getInvitationEmailError(invitationEmails)
  const inviteLabel =
    invitationEmails.length > 1 ? "Send Invites" : "Send Invite"
  const showInviteValidation =
    !!invitationEmailError && !busy && !refreshing && canManage
  const inFlight = useRef(false)
  const generation = useRef(0)
  useEffect(
    () => () => {
      generation.current += 1
    },
    [],
  )

  const members = data.members
  const invitations = data.invitations
  const lastAdminId =
    adminCount === 1
      ? members.find((member) => member.role === "org:admin")?.id
      : undefined

  const runAction = async (
    action: () => Promise<void>,
    message: string,
    onSuccess?: () => void,
  ) => {
    if (inFlight.current || !canManage || refreshing) {
      return
    }
    inFlight.current = true
    const requestGeneration = generation.current
    setBusy(true)
    setError("")
    try {
      await action()
      if (generation.current !== requestGeneration) {
        return
      }
      toast.success({ title: message })
      onSuccess?.()
    } catch (actionError) {
      if (
        generation.current !== requestGeneration ||
        isReverificationCancelledError(actionError)
      ) {
        return
      }
      if (inviteOpen || removeTarget) {
        setError(
          actionError instanceof Error
            ? actionError.message
            : "Could not save this change. Please try again.",
        )
      } else {
        toast.error({
          title: "Could not update organization access",
          error: actionError,
          fallback: "Could not save this change. Please try again.",
        })
      }
    } finally {
      inFlight.current = false
      if (generation.current === requestGeneration) {
        setBusy(false)
      }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-coval-text">
            Members
          </h1>
          <p className="mt-2 text-sm text-coval-text-muted">
            Manage who belongs to your organization and what they can do.
          </p>
        </div>
        <Button
          onClick={() => {
            setInviteOpen(true)
            setError("")
            setEmails([])
            setEmailDraft("")
            setRole("org:member")
          }}
          disabled={busy || refreshing || !canManage}
        >
          <UserPlus size={16} />
          Invite
        </Button>
      </div>

      <Card>
        <CardContent className="flex items-start gap-3 p-4 text-sm text-coval-text-muted">
          <Shield size={16} className="mt-0.5 shrink-0" />
          <p>
            Admins manage organization settings and members. Members use the
            platform. Your organization must always have at least one admin.
          </p>
        </CardContent>
      </Card>

      {!canManage && memberCount !== undefined && (
        <OrganizationPermissionNotice
          reason="Your role can view members. An admin can invite people, change roles, or remove members."
          request="organization member management"
          disabled={busy}
        />
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="members">
            <Users size={15} />
            Members<Badge variant="outline">{memberCount ?? "…"}</Badge>
          </TabsTrigger>
          <TabsTrigger value="invitations">
            <Mail size={15} />
            Invitations
            <Badge variant="outline">
              {canManage ? (invitationCount ?? "…") : "Restricted"}
            </Badge>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="members">
          <div className="my-5 max-w-sm">
            <SearchInput
              aria-label="Search members"
              placeholder="Search by name, email, or User ID…"
              value={memberSearch}
              maxLength={254}
              onChange={(event) => onMemberSearchChange(event.target.value)}
              onClear={() => onMemberSearchChange("")}
            />
          </div>
          {membersState}
          {memberCount !== undefined && (
            <MemberTable
              members={members}
              currentUserId={currentUserId}
              canManage={canManage}
              busy={busy || refreshing}
              lastAdminId={lastAdminId}
              onRoleChange={(member, nextRole) => {
                void runAction(
                  () => actions.changeRole(member, nextRole),
                  `${member.name} now has the ${ORGANIZATION_ROLE_LABELS[nextRole]} role.`,
                )
              }}
              onRemove={(member) => {
                setError("")
                setRemoveTarget(member)
              }}
            />
          )}
          {membersPagination}
        </TabsContent>
        <TabsContent value="invitations">
          {canManage ? (
            invitationsState
          ) : (
            <OrganizationPermissionNotice
              reason="Pending invitations are only available to organization admins."
              request="pending organization invitations"
              disabled={busy}
            />
          )}
          {canManage && invitationCount !== undefined && (
            <ListTable aria-label="Pending invitations">
              <ListTableHeader>
                <ListTableRow>
                  <ListTableHead>Email</ListTableHead>
                  <ListTableHead>Role</ListTableHead>
                  <ListTableHead>Status</ListTableHead>
                  <ListTableHead>Invited</ListTableHead>
                  <ListTableHead>
                    <span className="sr-only">Actions</span>
                  </ListTableHead>
                </ListTableRow>
              </ListTableHeader>
              <ListTableBody>
                {invitations.map((invitation) => (
                  <ListTableRow key={invitation.id}>
                    <ListTableCell className="font-medium">
                      {invitation.email}
                    </ListTableCell>
                    <ListTableCell>
                      {invitation.role === "org:admin" ||
                      invitation.role === "org:member"
                        ? ORGANIZATION_ROLE_LABELS[invitation.role]
                        : invitation.role}
                    </ListTableCell>
                    <ListTableCell>
                      <Badge variant="warning">Pending</Badge>
                    </ListTableCell>
                    <ListTableCell className="whitespace-nowrap text-coval-text-muted">
                      {new Date(invitation.created_at).toLocaleDateString(
                        "en-US",
                        { month: "short", day: "numeric" },
                      )}
                    </ListTableCell>
                    <ListTableCell className="text-right">
                      <Button
                        variant="ghost"
                        disabled={busy || refreshing || !canManage}
                        onClick={() => {
                          void runAction(
                            () => actions.revoke(invitation),
                            `Invitation to ${invitation.email} revoked.`,
                          )
                        }}
                      >
                        Revoke
                      </Button>
                    </ListTableCell>
                  </ListTableRow>
                ))}
                {invitations.length === 0 && (
                  <ListTableRow>
                    <ListTableCell colSpan={5}>
                      <div className="flex flex-col items-center gap-2 py-10 text-coval-text-muted">
                        <Mail size={24} />
                        <span className="font-medium text-coval-text">
                          No pending invitations
                        </span>
                        <span>
                          Invite a teammate to join your organization.
                        </span>
                      </div>
                    </ListTableCell>
                  </ListTableRow>
                )}
              </ListTableBody>
            </ListTable>
          )}
          {canManage && invitationsPagination}
        </TabsContent>
      </Tabs>

      <Dialog
        open={inviteOpen}
        onOpenChange={(open) => {
          if (!busy) {
            setInviteOpen(open)
          }
        }}
      >
        <DialogContent size="md">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              if (invitationEmailError) {
                setError(invitationEmailError)
                return
              }
              void runAction(
                () => actions.invite(invitationEmails, role),
                invitationEmails.length === 1
                  ? `Invitation sent to ${invitationEmails[0]}.`
                  : `${invitationEmails.length} invitations sent.`,
                () => {
                  setInviteOpen(false)
                  setTab("invitations")
                  onMemberSearchChange("")
                },
              )
            }}
          >
            <DialogHeader>
              <DialogTitle>Invite new members</DialogTitle>
              <DialogDescription>
                Give your teammates access to your organization.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email addresses</Label>
                <p
                  id="invite-email-help"
                  className="text-sm text-coval-text-muted"
                >
                  Enter or paste one or more email addresses, separated by
                  spaces or commas.
                </p>
                <InvitationEmailInput
                  emails={emails}
                  draft={emailDraft}
                  onEmailsChange={(values) => {
                    setEmails(values)
                    setError("")
                  }}
                  onDraftChange={(value) => {
                    setEmailDraft(value)
                    setError("")
                  }}
                  disabled={busy || refreshing || !canManage}
                  invalid={!!error && !!invitationEmailError}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-role">Organization role</Label>
                <Select
                  value={role}
                  onValueChange={(value) => setRole(value as OrganizationRole)}
                  disabled={busy || refreshing || !canManage}
                >
                  <SelectTrigger
                    aria-label="Organization role"
                    id="invite-role"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="org:member">Member</SelectItem>
                    <SelectItem value="org:admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <OrganizationRoleCard role={role} />
              </div>
              {error && (
                <p role="alert" className="text-sm text-coval-danger">
                  {error}
                </p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setInviteOpen(false)}
              >
                Cancel
              </Button>
              <Tooltip content={invitationEmailError} side="bottom">
                <span
                  className="inline-flex rounded-md focus-visible:outline-none"
                  role={showInviteValidation ? "button" : undefined}
                  tabIndex={showInviteValidation ? 0 : undefined}
                  aria-disabled={showInviteValidation || undefined}
                  aria-label={showInviteValidation ? inviteLabel : undefined}
                  onClick={() => {
                    if (showInviteValidation) {
                      setError(invitationEmailError ?? "")
                    }
                  }}
                  onKeyDown={(event) => {
                    if (
                      showInviteValidation &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      event.preventDefault()
                      setError(invitationEmailError ?? "")
                    }
                  }}
                >
                  <Button
                    type="submit"
                    loading={busy}
                    disabled={
                      refreshing || !canManage || !!invitationEmailError
                    }
                    className={
                      showInviteValidation ? "pointer-events-none" : undefined
                    }
                    tabIndex={showInviteValidation ? -1 : undefined}
                    aria-hidden={showInviteValidation || undefined}
                  >
                    {busy ? "Sending…" : inviteLabel}
                  </Button>
                </span>
              </Tooltip>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open && !busy) {
            setRemoveTarget(null)
          }
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Remove {removeTarget?.name}?</DialogTitle>
            <DialogDescription>
              They will lose access to this organization and its workspaces. You
              can invite them again later.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <DialogBody>
              <p role="alert" className="text-sm text-coval-danger">
                {error}
              </p>
            </DialogBody>
          )}
          <DialogFooter>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => setRemoveTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={busy || refreshing || !canManage}
              onClick={() => {
                if (removeTarget) {
                  void runAction(
                    () => actions.remove(removeTarget),
                    `${removeTarget.name} removed from the organization.`,
                    () => setRemoveTarget(null),
                  )
                }
              }}
            >
              {busy ? "Removing…" : "Remove member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
